import * as moment from 'moment';
import { Injectable } from '@angular/core';
import { LocaleService } from 'mydatepicker/dist';
import { IMyDate, IMyOptions } from 'mydatepicker';
import { SchedulingSetting } from '../../shared/models/scheduling-setting';
import { SchedulingSettingPeriod } from '../../shared/models/scheduling-setting-period';

@Injectable()
export class SchedulingSettingsDatepickerService {
    private schedulingSettings: Array<SchedulingSetting> = [];

    readonly SUNDAY_WEEKDAY = 0;
    readonly MONDAY_WEEKDAY = 1;
    readonly TUESDAY_WEEKDAY = 2;
    readonly WEDNESDAY_WEEKDAY = 3;
    readonly THURSDAY_WEEKDAY = 4;
    readonly FRIDAY_WEEKDAY = 5;
    readonly SATURDAY_WEEKDAY = 6;
    readonly MONDAY_DATEPICKER = 'mo';
    readonly TUESDAY_DATEPICKER = 'tu';
    readonly WEDNESDAY_DATEPICKER = 'we';
    readonly THURSDAY_DATEPICKER = 'th';
    readonly FRIDAY_DATEPICKER = 'fr';
    readonly SATURDAY_DATEPICKER = 'sa';
    readonly SUNDAY_DATEPICKER = 'su';

    constructor(
        protected localeService: LocaleService
    ) {}

    public buildDatePickerOptions(): IMyOptions {
        const disableDays$: Array<IMyDate> = this.buildDisableDays();
        const disableUntil$: IMyDate = this.buildDisableUntil();
        const disableSince$: IMyDate = this.buildDisableSince();
        const disableWeekdays$ = this.buildDisableWeekdays();
        const ptOptions = this.localeService.getLocaleOptions('pt-br');

        return {
            inline: true,
            dateFormat: ptOptions.dateFormat,
            dayLabels: ptOptions.dayLabels,
            monthLabels: ptOptions.monthLabels,
            todayBtnTxt: ptOptions.todayBtnTxt,
            firstDayOfWeek: ptOptions.firstDayOfWeek,
            sunHighlight: ptOptions.sunHighlight,
            markWeekends: {marked: false, color: 'gray'},
            disableUntil: disableUntil$,
            disableSince: disableSince$,
            disableDays: disableDays$,
            disableWeekdays: disableWeekdays$,
            disableWeekends: false
        };
    }
    public definePossibleSchedulingSettings(deliveryDate: string) {
        const date = moment(deliveryDate, 'YYYY-MM-DD');
        const isPossibleWeekday = (
            weekday: number,
            enabled: boolean
        ) => date.weekday() === weekday && enabled;

        return this.getSchedulingSettings().filter(
            (setting: SchedulingSetting) => {
                const exceptionalDate = setting.exceptional_dates
                    .filter(exceptional => exceptional.date === deliveryDate);
                const hasSomePossibleWeekday = isPossibleWeekday(this.MONDAY_WEEKDAY, setting.mondays)
                    || isPossibleWeekday(this.TUESDAY_WEEKDAY, setting.tuesdays)
                    || isPossibleWeekday(this.WEDNESDAY_WEEKDAY, setting.wednesdays)
                    || isPossibleWeekday(this.THURSDAY_WEEKDAY, setting.thursdays)
                    || isPossibleWeekday(this.FRIDAY_WEEKDAY, setting.fridays)
                    || isPossibleWeekday(this.SATURDAY_WEEKDAY, setting.saturdays)
                    || isPossibleWeekday(this.SUNDAY_WEEKDAY, setting.sundays);

                return exceptionalDate.length === 0 && hasSomePossibleWeekday;
            }
        );
    }

    private buildDisableWeekdays(): Array<string> {
        const enabled = []; // mo, tu, we, th, fr, sa, su
        let disabled = []; // mo, tu, we, th, fr, sa, su

        const enable = (day) => {
            disabled = disabled.filter(disabledDay => disabledDay !== day);
            enabled.push(day);
        };
        const handleDay =  (day, isEnabled) => {
            const isAlreadyEnabled = enabled.indexOf(day) >= 0;

            if (isEnabled || isAlreadyEnabled) {
                enable(day);
            } else {
                disabled.push(day);
            }
        };

        this.schedulingSettings.forEach(setting => {
            handleDay(this.MONDAY_DATEPICKER, setting.mondays);
            handleDay(this.TUESDAY_DATEPICKER, setting.tuesdays);
            handleDay(this.WEDNESDAY_DATEPICKER, setting.wednesdays);
            handleDay(this.THURSDAY_DATEPICKER, setting.thursdays);
            handleDay(this.FRIDAY_DATEPICKER, setting.fridays);
            handleDay(this.SATURDAY_DATEPICKER, setting.saturdays);
            handleDay(this.SUNDAY_DATEPICKER, setting.sundays);
        });

        return disabled;
    }
    private buildDisableDays(): Array<IMyDate> {
        const days = [];
        this.schedulingSettings
            .forEach(setting => setting['exceptional_dates'].forEach(exceptionalDate => {
                const date = moment(exceptionalDate.date, 'YYYY-MM-DD');

                days.push({
                    year: date.year(),
                    month: (date.month() + 1),
                    day: date.date()
                });
            }));

        if (false === this.todayIsAvailable()) {
            const now = moment();
            days.push({
                year: now.year(),
                month: (now.month() + 1),
                day: now.date()
            });
        }

        return days;
    }

    private todayIsAvailable() {
        let periods = 0;
        this.schedulingSettings.forEach(settings => {
            periods += settings.periods.length;
        });

        let possiblePeriods = 0;
        this.schedulingSettings.forEach(settings => {
            settings.periods.forEach((period: SchedulingSettingPeriod) => {
                const disabledMinutesToHideBeforeEnd = period.minutes_to_hide_before_end === null
                    || parseInt(period.minutes_to_hide_before_end) === 0;
                const disabledMinutesToShowAfterStart = period.minutes_to_show_after_start === null
                    || parseInt(period.minutes_to_show_after_start) === 0;

                if (disabledMinutesToHideBeforeEnd && disabledMinutesToShowAfterStart) {
                    possiblePeriods += 1;
                    return;
                }

                const now = moment();
                const startParts = period.start_at.split(':');
                const startHour = parseInt(startParts.shift(), null);
                const startMinute = parseInt(startParts.pop(), null);
                const possibleStart = moment().hours(startHour).minutes(startMinute);
                possibleStart.add(period.minutes_to_show_after_start, 'minute');

                const endParts = period.end_at.split(':');
                const endHour = parseInt(endParts.shift(), null);
                const endMinute = parseInt(endParts.pop(), null);
                const possibleEnd = moment().hours(endHour).minutes(endMinute);
                possibleEnd.subtract(period.minutes_to_hide_before_end, 'minute');

                if (
                    (disabledMinutesToShowAfterStart || now.isAfter(possibleStart))
                    && (disabledMinutesToHideBeforeEnd || now.isBefore(possibleEnd))
                ) {
                    possiblePeriods += 1;
                }
            });
        });

        return periods === 0 || possiblePeriods > 0;
    }

    private buildDisableUntil(): IMyDate {
        const starts = this.schedulingSettings.map(setting => setting.days_after_order_start);
        const lowestStart = Math.min(...starts);
        const now = moment();

        // Sub 1 day because this method define "disableUntil" property, so
        // the calender must to be disabled until one day before first
        // possible day to start schedule.
        now.add(lowestStart - 1, 'days');

        return {
            year: now.year(),
            month: (now.month() + 1),
            day: now.date()
        };
    }
    private buildDisableSince(): IMyDate {
        const now = moment();

        const starts = this.schedulingSettings.map(setting => setting.days_after_order_start);
        const lowestStart = Math.min(...starts);
        now.add(lowestStart, 'days');

        const limits = this.schedulingSettings.map(setting => setting.days_after_order_limit);
        const highestLimit = Math.max(...limits);

        // Add 1 day because this method define "disableSince" property, so
        // the calender must to be disabled since one day after last possible
        // day to end schedule.
        now.add(highestLimit + 1, 'days');

        return {
            year: now.year(),
            month: (now.month() + 1),
            day: now.date()
        };
    }

    public getSchedulingSettings(): Array<SchedulingSetting> {
        return this.schedulingSettings;
    }
    public setSchedulingSettings(value: Array<SchedulingSetting>) {
        this.schedulingSettings = value;

        return this;
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src3048926480/src/src/app/payment/services/scheduling-settings-datepicker.service.ts